import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationService from '../../services/NotificationService';

// Custom SVG Icons
const BellIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
);

const CheckCircleIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const ExclamationTriangleIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
    </svg>
);

const InformationCircleIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const UniversityIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
);


const getIconComponent = (iconString) => {
    const iconMap = {
        'fas fa-check-circle': <CheckCircleIcon className="w-5 h-5 text-green-500" />,
        'fas fa-times-circle': <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />,
        'fas fa-exclamation-triangle': <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />,
        'fas fa-university': <UniversityIcon className="w-5 h-5 text-blue-500" />,
        'fas fa-info-circle': <InformationCircleIcon className="w-5 h-5 text-blue-500" />,
    };
    
    return iconMap[iconString] || <InformationCircleIcon className="w-5 h-5 text-gray-500" />;
};


const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
};

const NotificationBell = () => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    
    useEffect(() => {
        fetchNotifications();
    }, []);

    
    useEffect(() => {
        const interval = setInterval(() => {
            fetchNotifications();
        }, 60000); // 60 seconds
        
        return () => {
            if (interval) clearInterval(interval);
        };
    }, []);

    
    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    const fetchNotifications = async () => {
        try {
            setIsLoading(true);
            const response = await NotificationService.getNotifications('notification');
            
            console.log('🔔 Notifications response:', response);
            
          
            if (response && response.Notifications) {
                setNotifications(response.Notifications);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const unreadCount = notifications.filter(n => !n.IsRead).length;

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    
    const extractLoanNumber = (body) => {
        if (!body) return null;
        const match = body.match(/loan\s+(\d+)/i);
        return match ? match[1] : null;
    };

    
    const isBankDetailNotification = (notification) => {
        const bankDetailKeywords = [
            'Bank Detail',
            'Bank Details',
            'bank detail change',
            'nominated bank account'
        ];
        
        return bankDetailKeywords.some(keyword => 
            notification.Title?.includes(keyword) || 
            notification.Body?.includes(keyword)
        );
    };

    
    const isPaymentApprovalNotification = (notification) => {
        const paymentKeywords = [
            'Payment Request',
            'payment request',
            'requires your approval',
            'waiting for your review',
            'waiting for your approval',
            'requires authorization'
        ];
        
        return paymentKeywords.some(keyword => 
            notification.Title?.includes(keyword) || 
            notification.Body?.includes(keyword)
        ) || notification.Type === 'payment_approval';
    };

    // Extract payment information from notification body
    const extractPaymentInfo = (notification) => {
        // Try multiple patterns to extract the loan/account number
        // Pattern 1: "loan 35906762" or "loan: 35906762"
        let loanMatch = notification.Body?.match(/loan[:\s]+(\d+)/i);
        
        // Pattern 2: "Account: 35906762" or "Account 35906762"
        if (!loanMatch) {
            loanMatch = notification.Body?.match(/Account[:\s]+(\d+)/i);
        }
        
        // Pattern 3: Extract from parentheses like "(Account: 35906762)"
        if (!loanMatch) {
            loanMatch = notification.Body?.match(/\(.*?Account[:\s]+(\d+)\)/i);
        }
        
        const loanNumber = loanMatch ? loanMatch[1] : null;
        
        // Extract payment type from [PaymentType:XXX] tag
        const paymentTypeMatch = notification.Body?.match(/\[PaymentType:([^\]]+)\]/i);
        const paymentType = paymentTypeMatch ? paymentTypeMatch[1] : null;
        
        // Extract request ID from [RequestID:XXX] tag
        const requestIdMatch = notification.Body?.match(/\[RequestID:(\d+)\]/i);
        const paymentRequestId = requestIdMatch ? requestIdMatch[1] : null;
        
        // Determine account type based on payment type
        // Redraw -> Loan account (shows in Loan Transactions tab)
        // BPay, Pay Anyone, Pay Me, Transfer -> Offset account (shows in Offset Transactions tab)
        const accountType = paymentType === 'Redraw' ? 'Loan' : 'Offset';
        
        console.log('🔍 Extracted payment info:', {
            body: notification.Body,
            loanNumber,
            paymentType,
            paymentRequestId,
            accountType
        });
        
        return { 
            loanNumber, 
            paymentRequestId, 
            paymentType, 
            accountType 
        };
    };

    const cleanNotificationBody = (body) => {
        if (!body) return '';

        return body
            .replace(/\[PaymentType:[^\]]+\]/gi, '')
            .replace(/\[RequestID:\d+\]/gi, '')
            .trim();
    };

    const handleNotificationClick = async (notification) => {
        console.log('🔔 Notification clicked:', notification);
        
        // Mark as read first
        await markAsRead(notification.Id);
        
        // Close dropdown
        setIsOpen(false);
        
        if (isBankDetailNotification(notification)) {
            const loanNumber = extractLoanNumber(notification.Body);
            console.log('🏦 Bank detail notification - Loan Number:', loanNumber);
            
            if (loanNumber) {
                navigate('/bank-details', { state: { loanAccount: loanNumber } });
            } else {
                navigate('/bank-details');
            }
        }
        
        // Check if this is a payment approval notification
        else if (isPaymentApprovalNotification(notification)) {
            const { loanNumber, paymentRequestId, paymentType, accountType } = extractPaymentInfo(notification);
            
            console.log('💰 Payment approval notification detected:', {
                loanNumber,
                paymentRequestId,
                paymentType,
                accountType,
                notificationBody: notification.Body,
                notificationType: notification.Type
            });
            
            if (loanNumber) {
                const navigationState = { 
                    accountHash: loanNumber,  // For selecting the correct account in dropdown
                    loanAccount: loanNumber,
                    accountType: accountType, 
                    showPendingTransaction: true,
                    paymentRequestId: paymentRequestId,
                    paymentType: paymentType
                };
                
                console.log('🚀 Navigating to /accounts with state:', navigationState);
                navigate('/accounts', { state: navigationState });
            } else {
                console.warn('⚠️ No loan number found in payment notification, navigating without state');
                navigate('/accounts');
            }
        } else {
            console.log('ℹ️ Generic notification - no specific navigation');
        }
    };

    const markAsRead = async (id) => {
        try {
            await NotificationService.markAsRead(id);
            
    
            setNotifications(prevNotifications =>
                prevNotifications.map(notification =>
                    notification.Id === id
                        ? { ...notification, IsRead: true }
                        : notification
                )
            );
            
            console.log('✅ Notification marked as read:', id);
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const clearAll = async () => {
        try {
    
            const unreadNotifications = notifications.filter(n => !n.IsRead);
            
            for (const notification of unreadNotifications) {
                await NotificationService.markAsRead(notification.Id);
            }
            
    
            setNotifications(prevNotifications =>
                prevNotifications.map(notification => ({ ...notification, IsRead: true }))
            );
            
            console.log('✅ All notifications marked as read');
        } catch (error) {
            console.error('Error clearing notifications:', error);
        }
        
        setIsOpen(false);
    };

    return (
        <div className="relative">
            {/* Notification Bell Button */}
            <button 
                onClick={toggleDropdown}
                className="relative p-2 text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
            >
                <BellIcon className="w-6 h-6" />
                {unreadCount > 0 && (
                    <span className="absolute -top-0 -right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div 
                        className="fixed inset-0 z-30" 
                        onClick={() => setIsOpen(false)}
                    ></div>
                    
                    {/* Dropdown Panel */}
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-40 max-h-96 overflow-hidden">
                        {/* Header */}
                        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                            <button
                                onClick={clearAll}
                                className="text-sm text-blue-600 hover:text-blue-800"
                            >
                                Clear All
                            </button>
                        </div>

                        {/* Notifications List */}
                        <div className="overflow-y-auto max-h-80">
                            {isLoading ? (
                                <div className="px-4 py-6 text-center text-gray-500">
                                    Loading notifications...
                                </div>
                            ) : notifications.length > 0 ? (
                                notifications.map((notification) => (
                                    <div 
                                        key={notification.Id}
                                        className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                                            !notification.IsRead ? 'bg-blue-50' : ''
                                        }`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="flex items-start space-x-3">
                                            {/* Icon */}
                                            <div className="flex-shrink-0 pt-0.5">
                                                {getIconComponent(notification.Icon)}
                                            </div>
                                            
                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start">
                                                    <p className={`text-sm font-medium ${
                                                        !notification.IsRead ? 'text-gray-900' : 'text-gray-700'
                                                    }`}>
                                                        {notification.Title}
                                                    </p>
                                                    {!notification.IsRead && (
                                                        <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1"></div>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-600 mt-1">
                                                    {cleanNotificationBody(notification.Body)}
                                                </p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {formatTimeAgo(notification.CreatedDate)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="px-4 py-6 text-center text-gray-500">
                                    No notifications
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {notifications.length > 0 && (
                            <div className="px-4 py-3 border-t border-gray-200 text-center">
                                <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                                    View All Notifications
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default NotificationBell;